import type { TTokenResponse } from "@/api/backoffice/login/backoffice-login-api.ts";
import type { TPersonAccessDto } from "@/types/backoffice/dto/person/person-access-dto";
import type { TPersonDto } from "@/types/backoffice/dto/person/person-dto";

import { useMutation } from "@tanstack/vue-query";
import { z } from "zod";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { authenticationMutationKeys } from "@/api/backoffice/login/authentication-query-keys.ts";
import { useEnv } from "@/hooks/use-env";
import {
  retrieveTokenDetailsFromLocalStorage,
  saveTokenDetailsInLocalStorage,
} from "@/lib/helpers/authentication-helper.ts";
import { useAuthenticationStore } from "@/stores/authentication/authentication-store.ts";

export const loginFormSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

export interface LoginResult {
  token: TTokenResponse;
  me: TPersonDto;
  accesses: TPersonAccessDto[];
  hasOperatorExternalEndCallService: boolean;
}

export async function loginFromStorage(): Promise<LoginResult | null> {
  const authenticationStore = useAuthenticationStore();
  const savedTokenDetails = retrieveTokenDetailsFromLocalStorage();

  if (!savedTokenDetails) {
    return null;
  }

  const { refresh_token: refreshToken } = savedTokenDetails;

  const tokenDetails = await BackofficeApi.refreshToken({
    refresh_token: refreshToken,
  });

  saveTokenDetailsInLocalStorage(tokenDetails);

  const me = await BackofficeApi.getMe();
  const accesses = await BackofficeApi.findOperatorAccess();
  const hasOperatorExternalEndCallService = await BackofficeApi.isOperatorHasExternalEndCallService();

  authenticationStore.login({
    me,
    accesses,
    hasOperatorExternalEndCallService,
  });

  return {
    token: tokenDetails,
    me,
    accesses,
    hasOperatorExternalEndCallService,
  };
}

export default function useLogin() {
  const authenticationStore = useAuthenticationStore();
  const env = useEnv();

  const loginMutation = useMutation({
    mutationKey: authenticationMutationKeys.login,
    mutationFn: async (values: z.infer<typeof loginFormSchema>): Promise<LoginResult> => {
      let tokenDetails!: TTokenResponse;

      if (env.VITE_IS_GOOGLE_CAPTCHA_ENABLED) {
        const token = await grecaptcha.enterprise.execute(env.VITE_GOOGLE_CAPTCHA_SITE_KEY, { action: "login" });
        const captchaClient = env.VITE_GOOGLE_CAPTCHA_SITE_KEY;

        tokenDetails = await BackofficeApi.externalLogin(
          {
            username: values.username,
            password: values.password,
          },
          {
            headers: {
              "x-captcha-client": captchaClient,
              "x-captcha-token": token,
            },
          },
        );
      } else {
        tokenDetails = await BackofficeApi.login({
          username: values.username,
          password: values.password,
        });
      }

      saveTokenDetailsInLocalStorage(tokenDetails);

      const me = await BackofficeApi.getMe();
      const accesses = await BackofficeApi.findOperatorAccess();
      const hasOperatorExternalEndCallService = await BackofficeApi.isOperatorHasExternalEndCallService();

      authenticationStore.login({
        me,
        accesses,
        hasOperatorExternalEndCallService,
      });

      return {
        token: tokenDetails,
        me,
        accesses,
        hasOperatorExternalEndCallService,
      };
    },
  });

  const loginFromStorageMutation = useMutation({
    mutationKey: authenticationMutationKeys.loginFromStorage,
    mutationFn: loginFromStorage,
  });

  return {
    login: loginMutation,
    loginFromStorage: loginFromStorageMutation,
  };
}
